import React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import EventApprovalCard from '../components/EventApprovalCard';
import { VOLUNTEER_CATEGORIES } from '../constants/volunteerCategories';
import {
  type EventRequest,
  useEventRequests,
  useSyncEventRequests,
  useUpdateEventRequest,
} from '../hooks/useEventRequests';
import { useCreateEvent, useDeleteEvent, useEvents, useUpdateEvent } from '../hooks/useEvents';
import {
  compareEventsByStartDateAsc,
  compareEventsByStartDateDesc,
  isEventInPastWindow,
  isEventPastToday,
  PAST_EVENTS_MONTHS,
  toUtcIsoDate,
} from '../utils/eventDateWindow';
import { useSyncMeta } from '../hooks/useSyncMeta';
import { useVolunteerCategories } from '../hooks/useVolunteerCategories';
import { queryClient } from '../queryClient';
import { supabase } from '../services/supabase';
import { useAuth } from '../context/AuthContext';
import { useCurrentUserProfile } from '../hooks/useCurrentUserProfile';
import { useMyEventAdminEventIds } from '../hooks/useEventAdmins';
import { Permission, hasPermission } from '../utils/roles';
import { EventMediaEditor } from '../components/EventMediaEditor';
import { EventMediaItem, getPrimaryImageUrl } from '../utils/eventMedia';
import { replaceSearchParamsSafely } from '../utils/safeSearchParamReplace';

const MOBILE_BREAKPOINT = '768px';

function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = React.useState(
    () => (typeof window !== 'undefined' ? window.matchMedia(query).matches : false)
  );
  React.useEffect(() => {
    const m = window.matchMedia(query);
    const handler = () => setMatches(m.matches);
    m.addEventListener('change', handler);
    setMatches(m.matches);
    return () => m.removeEventListener('change', handler);
  }, [query]);
  return matches;
}

const Page = styled.div`
  background: var(--background);
  padding: 1.5rem 2rem 2rem;
  min-height: 100%;
  color: var(--onSurface);

  @media (max-width: ${MOBILE_BREAKPOINT}) {
    padding: 1.5rem 1rem 2rem;
  }
`;

const Section = styled.div`
  margin-top: 2.25rem;
`;

const SectionTitle = styled.h2`
  margin: 0;
  font-size: 1.2rem;
  color: var(--onSurface);
`;

const PageTitle = styled.h1`
  margin: 0;
  font-size: 1.75rem;
  font-weight: 700;
  color: var(--onSurface);
`;

const SectionHeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1.25rem;
`;

export const EventsPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const { data: profile } = useCurrentUserProfile(user?.id);
  const { data: assignedEventIds = [] } = useMyEventAdminEventIds(user?.id);
  const roles = profile?.roles ?? user?.user_metadata?.roles;
  const canManageEvents = hasPermission(roles, Permission.MANAGE_EVENTS);
  const assignedEventIdSet = React.useMemo(() => new Set(assignedEventIds), [assignedEventIds]);
  const isEventAdminOnly = !canManageEvents && assignedEventIdSet.size > 0;
  const isMobile = useMediaQuery(`(max-width: ${MOBILE_BREAKPOINT})`);

  const handleOpenEvent = React.useCallback(
    (eventId: string) => {
      if (canManageEvents) {
        navigate(`/events/setup/${eventId}`);
        return;
      }
      navigate(`/events/public-registrations/${eventId}`);
    },
    [canManageEvents, navigate]
  );
  const requestedTab = searchParams.get('tab');
  const [activeTab, setActiveTabState] = React.useState<TabKey>(() =>
    isEventsTabKey(requestedTab) ? requestedTab : 'pending'
  );
  const [eventStatusFilter, setEventStatusFilter] = React.useState<EventStatusFilter>('all');
  const [newEventForm, setNewEventForm] = React.useState({
    title: '',
    description: '',
    image_url: '',
    media_items: [] as EventMediaItem[],
    start_date: '',
    start_time: '',
    location: '',
    ministry: '',
    status: 'published',
  });
  const [createEventError, setCreateEventError] = React.useState<string | null>(null);
  const [isMinistryDropdownOpen, setIsMinistryDropdownOpen] = React.useState(false);
  const [isUploadingCreateEventImage, setIsUploadingCreateEventImage] = React.useState(false);
  const [viewMode, setViewMode] = React.useState<'list' | 'calendar'>('list');
  const [searchQuery, setSearchQuery] = React.useState('');
  const [isCreateModalOpen, setIsCreateModalOpen] = React.useState(false);
  const [openEventMenuId, setOpenEventMenuId] = React.useState<string | null>(null);
  const [deletingEventId, setDeletingEventId] = React.useState<string | null>(null);
  const upcomingStripRef = React.useRef<HTMLDivElement>(null);
  const [calendarDate, setCalendarDate] = React.useState(() => new Date());
  const [calendarHiddenStatuses, setCalendarHiddenStatuses] = React.useState<Set<TabKey>>(() => new Set());
  const [calendarPreviewRequest, setCalendarPreviewRequest] = React.useState<EventRequest | null>(null);
  const markedCompletedRef = React.useRef<Set<string>>(new Set());
  const focusedRequestId = searchParams.get('requestId')?.trim() ?? '';
  const replaceTabInUrl = React.useCallback(
    (nextTab: TabKey) => {
      const currentParams = searchParams instanceof URLSearchParams ? searchParams : new URLSearchParams();
      const nextParams = new URLSearchParams(currentParams);
      if (nextParams.get('tab') === nextTab) return;
      nextParams.set('tab', nextTab);
      replaceSearchParamsSafely(setSearchParams, currentParams, nextParams, {
        actionName: 'syncEventsTabQuery',
        requestId: focusedRequestId || null,
        tab: nextTab,
      });
    },
    [focusedRequestId, searchParams, setSearchParams]
  );
  const setActiveTab = React.useCallback(
    (nextTab: TabKey) => {
      setActiveTabState(nextTab);
      replaceTabInUrl(nextTab);
    },
    [replaceTabInUrl]
  );

  const {
    data: eventRequests,
    isLoading: isLoadingRequests,
    error: requestsError,
  } = useEventRequests();
  const { mutateAsync: syncRequests, isPending: isSyncing } = useSyncEventRequests();
  const { mutateAsync: updateRequest, isPending: isUpdating } = useUpdateEventRequest();
  const { mutateAsync: createEvent, isPending: isCreatingEvent } = useCreateEvent();
  const { mutateAsync: updateEvent } = useUpdateEvent();
  const { mutateAsync: deleteEvent, isPending: isDeletingEvent } = useDeleteEvent();
  const { data, isLoading, error } = useEvents({ includePastMonths: PAST_EVENTS_MONTHS });
  const todayIsoDate = React.useMemo(() => toUtcIsoDate(), []);
  const visibleEvents = React.useMemo(() => {
    if (!data) return [];
    if (!isEventAdminOnly) return data;
    return data.filter((event) => assignedEventIdSet.has(event.id));
  }, [assignedEventIdSet, data, isEventAdminOnly]);
  const eventOnlyRows = React.useMemo(
    () => visibleEvents.filter((event) => !isGeneralAnnouncementCategory(event.category)),
    [visibleEvents]
  );
  const upcomingEventRows = React.useMemo(
    () =>
      eventOnlyRows
        .filter((event) => !isEventPastToday(event, todayIsoDate))
        .slice()
        .sort(compareEventsByStartDateAsc),
    [eventOnlyRows, todayIsoDate]
  );
  const pastEventRows = React.useMemo(
    () =>
      eventOnlyRows
        .filter((event) => isEventInPastWindow(event, todayIsoDate, PAST_EVENTS_MONTHS))
        .slice()
        .sort(compareEventsByStartDateDesc),
    [eventOnlyRows, todayIsoDate]
  );
  const { data: syncMeta } = useSyncMeta();
  const { data: volunteerCategories } = useVolunteerCategories();

  const ministryOptions = React.useMemo(() => {
    const categories =
      volunteerCategories && volunteerCategories.length > 0
        ? volunteerCategories.map((category) => category.label).filter(Boolean)
        : VOLUNTEER_CATEGORIES.map((category) => category.label).filter(Boolean);
    return Array.from(new Set(categories)).sort((a, b) => a.localeCompare(b));
  }, [volunteerCategories]);

  const filteredMinistryOptions = React.useMemo(() => {
    const query = newEventForm.ministry.trim().toLowerCase();
    if (!query) return ministryOptions;
    return ministryOptions.filter((option) => option.toLowerCase().includes(query));
  }, [newEventForm.ministry, ministryOptions]);

  const filteredEvents = React.useMemo(() => {
    if (eventStatusFilter === 'past') return pastEventRows;
    if (eventStatusFilter === 'all') return upcomingEventRows;
    return upcomingEventRows.filter((event) => getEventStatusBucket(event.status) === eventStatusFilter);
  }, [eventStatusFilter, pastEventRows, upcomingEventRows]);

  const eventStatusTabCounts = React.useMemo(
    () =>
      ({
        all: upcomingEventRows.length,
        published: upcomingEventRows.filter((event) => getEventStatusBucket(event.status) === 'published').length,
        draft: upcomingEventRows.filter((event) => getEventStatusBucket(event.status) === 'draft').length,
        past: pastEventRows.length,
      }) satisfies Record<EventStatusFilter, number>,
    [pastEventRows, upcomingEventRows]
  );

  const tabCounts = React.useMemo(() => {
    if (!eventRequests) return {} as Record<TabKey, number>;
    const counts: Record<string, number> = {};
    TABS.forEach((tab) => {
      counts[tab.key] = eventRequests.filter((req) => isInTab(req, tab.key)).length;
    });
    return counts as Record<TabKey, number>;
  }, [eventRequests]);

  const statusSummary = React.useMemo(() => {
    if (!eventRequests) return { actionRequired: 0, approved: 0, onHold: 0 };
    const pending = eventRequests.filter((r) => isInTab(r, 'pending')).length;
    const approved = eventRequests.filter((r) => isInTab(r, 'approvals')).length;
    const onHold = eventRequests.filter((r) => isInTab(r, 'on_hold')).length;
    return { actionRequired: pending, approved, onHold };
  }, [eventRequests]);

  React.useEffect(() => {
    if (isEventAdminOnly || !eventRequests?.length) return;
    const toComplete = eventRequests.filter(
      (req) =>
        isEventDatePast(req) &&
        (req.status ?? '').toLowerCase() !== 'completed' &&
        !markedCompletedRef.current.has(req.id)
    );
    toComplete.forEach((req) => {
      markedCompletedRef.current.add(req.id);
      updateRequest({ id: req.id, status: 'completed' }).catch(() => {
        markedCompletedRef.current.delete(req.id);
      });
    });
  }, [eventRequests, isEventAdminOnly, updateRequest]);

  const searchFilteredRequests = React.useMemo(() => {
    const list = eventRequests ?? [];
    if (!searchQuery.trim()) return list;
    const q = searchQuery.trim().toLowerCase();
    return list.filter(
      (req) =>
        (req.title ?? '').toLowerCase().includes(q) ||
        (req.ministry ?? '').toLowerCase().includes(q) ||
        (req.contact_name ?? '').toLowerCase().includes(q) ||
        (req.location ?? '').toLowerCase().includes(q) ||
        (req.description ?? '').toLowerCase().includes(q)
    );
  }, [eventRequests, searchQuery]);

  const handleStatusChange = async (
    id: string,
    status: 'approved' | 'declined' | 'needs_info' | 'pending',
    notes?: string,
    options?: { createEvent?: boolean; eventStatus?: string }
  ) => {
    try {
      await updateRequest({
        id,
        status,
        notes,
        createEvent: options?.createEvent,
        eventStatus: options?.eventStatus,
      });
    } catch (err) {
      console.error(err);
      window.alert('Could not update request. Please try again.');
    }
  };

  const handleOpenSetupFromSubmission = async (id: string, notes?: string) => {
    try {
      const request = eventRequests?.find((row) => row.id === id);
      if (request?.event_id) {
        navigate(`/events/setup/${request.event_id}`);
        return;
      }

      // Opening setup creates or links a draft without approving the request.
      const result = await updateRequest({
        id,
        notes,
        createEvent: true,
        image_url: request?.image_url ?? undefined,
      });

      if (result?.createdEventId) {
        navigate(`/events/setup/${result.createdEventId}`);
        return;
      }

      // General announcements are stored without a date, and older update-event-request
      // deployments skip creating their draft. Create one here so Event Setup can open
      // and the date can be added there.
      if (!isGeneralAnnouncementCategory(request?.event_type)) {
        window.alert('Event could not be prepared. Check title/date on the request.');
        return;
      }

      const startDate = (request?.start_date ?? '').toString().slice(0, 10) || toUtcIsoDate();
      const startTime = request?.start_time || '00:00';
      const created = await createEvent({
        title: request?.title?.trim() || 'Untitled Event',
        description: request?.description ?? '',
        image_url: request?.image_url ?? null,
        start_date: startDate,
        end_date: startDate,
        start_time: startTime,
        end_time: startTime,
        location: request?.location?.trim() || request?.ministry?.trim() || 'TBD',
        ministry: request?.ministry ?? null,
        category: 'General Announcement',
        status: 'draft',
        organizer_name: request?.contact_name ?? request?.ministry ?? 'TBD',
      });

      const { error: linkError } = await supabase
        .from('event_requests')
        .update({ event_id: created.id, updated_at: new Date().toISOString() })
        .eq('id', id);
      if (linkError) throw linkError;

      await queryClient.invalidateQueries({ queryKey: ['event-requests'] });
      navigate(`/events/setup/${created.id}`);
    } catch (err) {
      console.error(err);
      window.alert('Could not open event setup. Please try again.');
    }
  };

  const handleCreateEvent = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!newEventForm.title.trim() || !newEventForm.start_date) {
      setCreateEventError('Title and date are required.');
      return;
    }

    try {
      setCreateEventError(null);
      const created = await createEvent({
        title: newEventForm.title.trim(),
        description: newEventForm.description.trim() || null,
        image_url: getPrimaryImageUrl(newEventForm.media_items, newEventForm.image_url.trim() || null),
        media_items: newEventForm.media_items,
        start_date: newEventForm.start_date,
        start_time: newEventForm.start_time || null,
        end_time: newEventForm.start_time || null,
        location: newEventForm.location.trim() || null,
        ministry: newEventForm.ministry.trim() || null,
        status: newEventForm.status,
      });
      setNewEventForm({
        title: '',
        description: '',
        image_url: '',
        media_items: [],
        start_date: '',
        start_time: '',
        location: '',
        ministry: '',
        status: 'published',
      });
      setIsCreateModalOpen(false);
      navigate(`/events/setup/${created.id}`);
    } catch (err) {
      console.error(err);
      setCreateEventError(err instanceof Error ? err.message : 'Could not create event.');
    }
  };

  const uploadCreateEventImage = async (file: File) => {
    const ext = file.name.split('.').pop() || 'jpg';
    const fileName = `event-banners/${Date.now()}_${Math.random().toString(36).slice(2)}.${ext}`;
    const { error: uploadError } = await supabase.storage.from('post-images').upload(fileName, file, { upsert: false });
    if (uploadError) throw uploadError;
    const { data: publicUrlData } = supabase.storage.from('post-images').getPublicUrl(fileName);
    return publicUrlData.publicUrl;
  };

  const handleCreateEventImageUpload = async (file?: File) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setCreateEventError('Please select an image file.');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setCreateEventError('Please upload an image smaller than 5MB.');
      return;
    }

    setCreateEventError(null);
    setIsUploadingCreateEventImage(true);
    try {
      const imageUrl = await uploadCreateEventImage(file);
      setNewEventForm((prev) => {
        const nextItems = [
          ...prev.media_items,
          { type: 'image' as const, url: imageUrl, position: prev.media_items.length, thumbnail_url: null },
        ];
        return {
          ...prev,
          image_url: getPrimaryImageUrl(nextItems, imageUrl) ?? '',
          media_items: nextItems,
        };
      });
    } catch (err) {
      console.error(err);
      setCreateEventError(err instanceof Error ? err.message : 'Could not upload image.');
    } finally {
      setIsUploadingCreateEventImage(false);
    }
  };

  const handleAddCreateEventVideo = React.useCallback((url: string) => {
    const normalized = url.trim();
    if (!normalized) return;
    setNewEventForm((prev) => ({
      ...prev,
      media_items: [
        ...prev.media_items,
        { type: 'video', url: normalized, position: prev.media_items.length, thumbnail_url: null },
      ],
    }));
  }, []);

  const handleMoveCreateEventMedia = React.useCallback((index: number, direction: -1 | 1) => {
    setNewEventForm((prev) => {
      const targetIndex = index + direction;
      if (targetIndex < 0 || targetIndex >= prev.media_items.length) return prev;
      const nextItems = [...prev.media_items];
      const temp = nextItems[index];
      nextItems[index] = nextItems[targetIndex];
      nextItems[targetIndex] = temp;
      return {
        ...prev,
        image_url: getPrimaryImageUrl(nextItems, prev.image_url) ?? '',
        media_items: nextItems.map((item, idx) => ({ ...item, position: idx })),
      };
    });
  }, []);

  const handleRemoveCreateEventMedia = React.useCallback((index: number) => {
    setNewEventForm((prev) => {
      const nextItems = prev.media_items.filter((_, idx) => idx !== index).map((item, idx) => ({ ...item, position: idx }));
      return {
        ...prev,
        image_url: getPrimaryImageUrl(nextItems, null) ?? '',
        media_items: nextItems,
      };
    });
  }, []);

  const handleImageChange = async (id: string, imageUrl: string) => {
    try {
      const request = eventRequests?.find((req) => req.id === id);
      const currentStatus = request?.status ?? ('pending' as const);
      await updateRequest({ id, status: currentStatus, image_url: imageUrl });
      if (request?.event_id) {
        await updateEvent({ eventId: request.event_id, image_url: imageUrl });
      }
    } catch (err) {
      console.error(err);
      window.alert('Could not save the image. Please try again.');
    }
  };

  const handleDeleteEvent = async (eventId: string, eventTitle?: string | null) => {
    const label = eventTitle?.trim() || 'this event';
    const isConfirmed = window.confirm(`Delete ${label}? This action cannot be undone.`);
    if (!isConfirmed) return;

    try {
      setDeletingEventId(eventId);
      await deleteEvent(eventId);
      setOpenEventMenuId((current) => (current === eventId ? null : current));
    } catch (err) {
      console.error(err);
      window.alert('Could not delete event. Please try again.');
    } finally {
      setDeletingEventId(null);
    }
  };

  const handleSaveRequestEdits = async (
    id: string,
    payload: {
      title: string;
      description: string | null;
      ministry: string | null;
      event_type: string | null;
      location: string | null;
      start_date: string | null;
      start_time: string | null;
      share_date: string | null;
    }
  ) => {
    try {
      const request = eventRequests?.find((row) => row.id === id);
      const currentStatus = request?.status ?? ('pending' as const);
      await updateRequest({
        id,
        status: currentStatus,
        ...payload,
      });
    } catch (err) {
      console.error(err);
      window.alert('Could not save request edits. Please try again.');
    }
  };

  const handleSync = async () => {
    try {
      const result = await syncRequests();
      if (result?.upserted !== undefined) {
        window.alert(`Synced ${result.upserted} rows from Google Sheets.`);
      }
    } catch (err) {
      console.error('[SyncEventRequests] failed', err);
      const message = err instanceof Error ? err.message : 'Unknown error';
      window.alert(
        `Sync failed: ${message}. Check function logs (sync-event-requests) for errorId if present.`
      );
    }
  };

  const displayRequests = React.useMemo(() => {
    return searchFilteredRequests.filter((req) => isInTab(req, activeTab)).sort(sortByLatest);
  }, [searchFilteredRequests, activeTab]);

  React.useEffect(() => {
    if (!isEventsTabKey(requestedTab)) return;
    setActiveTabState((current) => (current === requestedTab ? current : requestedTab));
  }, [requestedTab]);

  const appliedFocusRequestIdRef = React.useRef<string | null>(null);
  React.useEffect(() => {
    if (!focusedRequestId) return;
    if (appliedFocusRequestIdRef.current === focusedRequestId) return;

    if (requestsError) {
      const errorKey = `error:${focusedRequestId}`;
      if (appliedFocusRequestIdRef.current === errorKey) return;
      appliedFocusRequestIdRef.current = errorKey;
      const message = requestsError instanceof Error ? requestsError.message : 'Unknown error';
      console.error('[EventsPage] Could not focus the event request because submissions failed to load', {
        actionName: 'focusEventRequest',
        recoverable: true,
        requestId: focusedRequestId,
        message,
      });
      return;
    }

    if (!eventRequests) return;

    appliedFocusRequestIdRef.current = focusedRequestId;
    const targetRequest = eventRequests.find((request) => request.id === focusedRequestId) ?? null;
    if (!targetRequest) {
      console.warn('[EventsPage] Focused event request was not found', {
        actionName: 'focusEventRequest',
        recoverable: true,
        requestId: focusedRequestId,
      });
      return;
    }

    const targetTab = getStatusKey(targetRequest);
    setActiveTabState(targetTab);
    replaceTabInUrl(targetTab);
  }, [eventRequests, focusedRequestId, replaceTabInUrl, requestsError]);

  React.useEffect(() => {
    if (!focusedRequestId || viewMode !== 'list') return;
    const element = document.getElementById(`event-request-${focusedRequestId}`);
    if (!element || typeof element.scrollIntoView !== 'function') return;
    try {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
    } catch (error) {
      console.error('[EventsPage] Could not scroll to the focused event request', {
        actionName: 'focusEventRequest',
        recoverable: true,
        requestId: focusedRequestId,
        message: error instanceof Error ? error.message : String(error),
      });
    }
  }, [displayRequests, focusedRequestId, viewMode]);

  const calendarWeeks = React.useMemo(
    () => getCalendarWeeks(calendarDate.getFullYear(), calendarDate.getMonth()),
    [calendarDate]
  );

  const eventsForDay = React.useCallback(
    (day: Date) => {
      const iso = toISODate(day);
      return (eventRequests ?? []).filter((req) => (req.start_date ?? '').slice(0, 10) === iso);
    },
    [eventRequests]
  );

  const toggleCalendarStatus = React.useCallback((key: TabKey) => {
    setCalendarHiddenStatuses((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const todayISODate = React.useMemo(() => toISODate(new Date()), []);

  return (
    <Page>
      <SectionHeader>
        <HeaderLeft>
          <PageTitle>Events</PageTitle>
        </HeaderLeft>
        <HeaderRight>
          {canManageEvents ? (
            <LastSync>
              Last sync: {syncMeta?.last_synced_at ? new Date(syncMeta.last_synced_at).toLocaleString(undefined, { timeZone: 'UTC' }) : 'Not yet'}
            </LastSync>
          ) : null}
          {canManageEvents ? (
            <SyncButton type="button" onClick={handleSync} disabled={isSyncing}>
              {isSyncing ? 'Syncing…' : 'Sync'}
            </SyncButton>
          ) : null}
          {canManageEvents ? (
          <ViewToggle>
            <ViewToggleBtn type="button" data-active={viewMode === 'list'} onClick={() => setViewMode('list')} aria-pressed={viewMode === 'list'}>
              <span aria-hidden>☰</span> List
            </ViewToggleBtn>
            <ViewToggleBtn type="button" data-active={viewMode === 'calendar'} onClick={() => setViewMode('calendar')} aria-pressed={viewMode === 'calendar'}>
              <span aria-hidden>📅</span> Calendar
            </ViewToggleBtn>
          </ViewToggle>
          ) : null}
          {canManageEvents ? (
            <CreateEventButton type="button" onClick={() => setIsCreateModalOpen(true)}>
              + Create New Event
            </CreateEventButton>
          ) : null}
        </HeaderRight>
      </SectionHeader>

      {viewMode === 'list' ? (
        <>
          <Section>
            <SectionHeaderRow>
              <SectionTitle>
                {eventStatusFilter === 'past' ? 'Past Events' : 'Upcoming Events'}
              </SectionTitle>
              <EventStatusTabs role="tablist" aria-label="Filter events by status">
                {EVENT_STATUS_TABS.map((tab) => (
                  <EventStatusTabButton
                    key={tab.key}
                    type="button"
                    role="tab"
                    aria-selected={eventStatusFilter === tab.key}
                    data-active={eventStatusFilter === tab.key}
                    onClick={() => setEventStatusFilter(tab.key)}
                  >
                    {tab.label}
                    {eventStatusTabCounts[tab.key] > 0 ? (
                      <EventStatusTabCount>{eventStatusTabCounts[tab.key]}</EventStatusTabCount>
                    ) : null}
                  </EventStatusTabButton>
                ))}
              </EventStatusTabs>
            </SectionHeaderRow>
            {eventStatusFilter === 'past' ? (
              <PastEventsHint>Showing completed events from the last {PAST_EVENTS_MONTHS} months.</PastEventsHint>
            ) : null}

            {isLoading ? (
              <UpcomingMessage>Loading events…</UpcomingMessage>
            ) : error ? (
              <UpcomingMessage>Error loading events</UpcomingMessage>
            ) : filteredEvents.length === 0 ? (
              <UpcomingMessage>
                {isEventAdminOnly
                  ? 'No assigned events yet.'
                  : eventStatusFilter === 'past'
                    ? `No past events in the last ${PAST_EVENTS_MONTHS} months.`
                    : eventStatusFilter === 'draft'
                    ? 'No draft events yet.'
                    : 'No upcoming events yet.'}
              </UpcomingMessage>
            ) : (
              <UpcomingScrollWrap>
                <ScrollArrowButton type="button" onClick={() => upcomingStripRef.current?.scrollBy({ left: -296, behavior: 'smooth' })} aria-label="Scroll left">
                  ‹
                </ScrollArrowButton>
                <ScrollArrowButton type="button" $right onClick={() => upcomingStripRef.current?.scrollBy({ left: 296, behavior: 'smooth' })} aria-label="Scroll right">
                  ›
                </ScrollArrowButton>
                <UpcomingStrip ref={upcomingStripRef}>
                {filteredEvents.map((e) => (
                  <UpcomingCard key={e.id}>
                    <UpcomingCardOpenButton type="button" onClick={() => handleOpenEvent(e.id)}>
                      <UpcomingImageWrapper>
                        {e.image_url ? (
                          <UpcomingImage src={e.image_url} alt={e.title ?? 'Event image'} />
                        ) : (
                          <UpcomingImagePlaceholder />
                        )}
                        <UpcomingDateBadge>
                          <span className="month">
                            {new Date(e.start_date).toLocaleDateString(undefined, { month: 'short', timeZone: 'UTC' })}
                          </span>
                          <span className="day">
                            {new Date(e.start_date).toLocaleDateString(undefined, { day: '2-digit', timeZone: 'UTC' })}
                          </span>
                        </UpcomingDateBadge>
                      </UpcomingImageWrapper>
                      <UpcomingBody>
                        <UpcomingTitle title={e.title ?? ''}>{e.title}</UpcomingTitle>
                        <UpcomingMeta>
                          <span>{formatTime(e.start_time)}</span>
                          <span>•</span>
                          <span>{e.location || 'TBD location'}</span>
                        </UpcomingMeta>
                        <UpcomingTagRow>
                          <UpcomingStatusTag $status={getDisplayedEventStatus(e.status, isEventPastToday(e, todayIsoDate))}>
                            {formatDisplayedEventStatus(e.status, isEventPastToday(e, todayIsoDate))}
                          </UpcomingStatusTag>
                          <UpcomingTag>{(e.category || 'Community').toUpperCase()}</UpcomingTag>
                          {e.ministry ? <UpcomingTag $variant="subtle">{e.ministry}</UpcomingTag> : null}
                        </UpcomingTagRow>
                      </UpcomingBody>
                    </UpcomingCardOpenButton>
                    {canManageEvents ? (
                    <UpcomingMenuWrap>
                      <UpcomingMenuButton
                        type="button"
                        aria-label="Event options"
                        onClick={() => setOpenEventMenuId((current) => (current === e.id ? null : e.id))}
                      >
                        ⋯
                      </UpcomingMenuButton>
                      {openEventMenuId === e.id ? (
                        <UpcomingMenuList>
                          <UpcomingMenuItem
                            type="button"
                            data-variant="danger"
                            onClick={() => void handleDeleteEvent(e.id, e.title)}
                            disabled={isDeletingEvent && deletingEventId === e.id}
                          >
                            {isDeletingEvent && deletingEventId === e.id ? 'Deleting…' : 'Delete event'}
                          </UpcomingMenuItem>
                        </UpcomingMenuList>
                      ) : null}
                    </UpcomingMenuWrap>
                    ) : null}
                  </UpcomingCard>
                ))}
                </UpcomingStrip>
              </UpcomingScrollWrap>
            )}
          </Section>

          {canManageEvents ? (
            <>
          <Divider />

          <Tabs>
            {TABS.map((tab) => (
              <TabButton key={tab.key} data-active={activeTab === tab.key} onClick={() => setActiveTab(tab.key)}>
                {tab.label}
                {tabCounts[tab.key] != null && tabCounts[tab.key] > 0 ? (
                  <TabBadge>{String(tabCounts[tab.key]).padStart(2, '0')}</TabBadge>
                ) : null}
              </TabButton>
            ))}
          </Tabs>

          <Toolbar>
            <SearchWrap>
              <SearchIcon aria-hidden>⌕</SearchIcon>
              <SearchInput
                type="search"
                placeholder={`Search ${activeTab === 'pending' ? 'pending' : activeTab} events...`}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </SearchWrap>
            <ToolbarFilterButton type="button">Filter</ToolbarFilterButton>
            <StatusSummary>
              <StatusSummaryLabel>STATUS</StatusSummaryLabel>
              <StatusSummaryBadge $variant="action">
                {String(statusSummary.actionRequired).padStart(2, '0')} ACTION REQUIRED
              </StatusSummaryBadge>
              <StatusSummaryBadge $variant="approved">
                {String(statusSummary.approved).padStart(2, '0')} APPROVED
              </StatusSummaryBadge>
              <StatusSummaryBadge $variant="onHold">
                {String(statusSummary.onHold).padStart(2, '0')} ON HOLD
              </StatusSummaryBadge>
            </StatusSummary>
          </Toolbar>
            </>
          ) : null}
        </>
      ) : null}

      {canManageEvents && viewMode === 'calendar' ? (
        <CalendarWrap>
          <CalendarHeaderRow>
            <CalendarMonthLabel>
              {calendarDate.toLocaleDateString(undefined, { month: 'long', year: 'numeric' })}
            </CalendarMonthLabel>
            <CalendarNav>
              <CalendarNavBtn
                type="button"
                onClick={() => setCalendarDate((d) => new Date(d.getFullYear(), d.getMonth() - 1))}
                aria-label="Previous month"
              >
                ‹
              </CalendarNavBtn>
              <CalendarTodayBtn type="button" onClick={() => setCalendarDate(new Date())}>
                Today
              </CalendarTodayBtn>
              <CalendarNavBtn
                type="button"
                onClick={() => setCalendarDate((d) => new Date(d.getFullYear(), d.getMonth() + 1))}
                aria-label="Next month"
              >
                ›
              </CalendarNavBtn>
            </CalendarNav>
            <CalendarLegend>
              {TABS.map((tab) => (
                <CalendarLegendButton
                  key={tab.key}
                  type="button"
                  $hidden={calendarHiddenStatuses.has(tab.key)}
                  onClick={() => toggleCalendarStatus(tab.key)}
                  aria-pressed={calendarHiddenStatuses.has(tab.key)}
                  aria-label={`${calendarHiddenStatuses.has(tab.key) ? 'Show' : 'Hide'} ${tab.label} events`}
                >
                  <CalendarLegendDot $color={STATUS_CALENDAR_COLORS[tab.key]} />
                  <span>{tab.label}</span>
                </CalendarLegendButton>
              ))}
            </CalendarLegend>
          </CalendarHeaderRow>
          <CalendarGrid>
            <CalendarWeekRow $header>
              {CALENDAR_DAY_NAMES.map((d, i) => (
                <CalendarDayHead key={d}>{isMobile ? CALENDAR_DAY_NAMES_SHORT[i] : d}</CalendarDayHead>
              ))}
            </CalendarWeekRow>
            {calendarWeeks.map((week, wi) => (
              <CalendarWeekRow key={wi}>
                {week.map(({ date, isCurrentMonth }) => {
                  const iso = toISODate(date);
                  const isToday = iso === todayISODate;
                  const dayEvents = eventsForDay(date).filter(
                    (req) => !calendarHiddenStatuses.has(getStatusKey(req))
                  );
                  return (
                    <CalendarDayCell key={iso} $faded={!isCurrentMonth}>
                      <CalendarDayNumber $today={isToday}>{date.getDate()}</CalendarDayNumber>
                      <CalendarDayEvents>
                        {dayEvents.map((req) => (
                          <CalendarEventChip
                            key={req.id}
                            $bg={STATUS_CALENDAR_COLORS[getStatusKey(req)]}
                            $text={STATUS_CALENDAR_TEXT[getStatusKey(req)]}
                            onClick={(e) => {
                              e.stopPropagation();
                              setCalendarPreviewRequest(req);
                            }}
                          >
                            {formatTime(req.start_time)} {truncateTitle(req.title ?? '')}
                          </CalendarEventChip>
                        ))}
                      </CalendarDayEvents>
                    </CalendarDayCell>
                  );
                })}
              </CalendarWeekRow>
            ))}
          </CalendarGrid>
        </CalendarWrap>
      ) : null}

      {canManageEvents && viewMode === 'list' && isLoadingRequests ? (
        <LoadingMessage>Loading requests…</LoadingMessage>
      ) : canManageEvents && viewMode === 'list' && requestsError ? (
        <LoadingMessage>Error loading requests</LoadingMessage>
      ) : canManageEvents && viewMode === 'list' && displayRequests.length > 0 ? (
        <CardList>
          {displayRequests.map((req) => (
            <RequestFocusWrap
              key={req.id}
              id={`event-request-${req.id}`}
              $highlighted={focusedRequestId === req.id}
            >
              <EventApprovalCard
                request={req}
                onApprove={(note) => handleStatusChange(req.id, 'approved', note)}
                onPublish={(note) => handleOpenSetupFromSubmission(req.id, note)}
                onDecline={(note) => handleStatusChange(req.id, 'declined', note)}
                onNeedInfo={(note) => handleStatusChange(req.id, 'needs_info', note)}
                onImageChange={(url) => handleImageChange(req.id, url)}
                onSaveEdits={(payload) => handleSaveRequestEdits(req.id, payload)}
                isUpdating={isUpdating || isCreatingEvent}
                isSavingEdits={isUpdating}
              />
            </RequestFocusWrap>
          ))}
        </CardList>
      ) : canManageEvents && viewMode === 'list' ? (
        <EmptyState>No event requests yet.</EmptyState>
      ) : null}

      {canManageEvents && isCreateModalOpen ? (
        <ModalOverlay onClick={() => setIsCreateModalOpen(false)}>
          <CreateModalContent onClick={(e) => e.stopPropagation()}>
            <CreateModalHeader>
              <h3>Create New Event</h3>
              <ModalCloseButton type="button" onClick={() => setIsCreateModalOpen(false)}>Close</ModalCloseButton>
            </CreateModalHeader>
            <ManageCard as="form" onSubmit={handleCreateEvent}>
              <EditorGrid>
                <Field>
                  <label>Title *</label>
                  <input value={newEventForm.title} onChange={(e) => setNewEventForm((p) => ({ ...p, title: e.target.value }))} placeholder="Community Night" required />
                </Field>
                <Field>
                  <label>Start Date *</label>
                  <input type="date" value={newEventForm.start_date} onChange={(e) => setNewEventForm((p) => ({ ...p, start_date: e.target.value }))} required />
                </Field>
                <Field>
                  <label>Start Time</label>
                  <input type="time" value={newEventForm.start_time} onChange={(e) => setNewEventForm((p) => ({ ...p, start_time: e.target.value }))} />
                </Field>
                <Field>
                  <label>Status</label>
                  <select value={newEventForm.status} onChange={(e) => setNewEventForm((p) => ({ ...p, status: e.target.value }))}>
                    <option value="published">Published</option>
                    <option value="draft">Draft</option>
                  </select>
                </Field>
              </EditorGrid>
              <EditorGrid>
                <Field>
                  <label>Location</label>
                  <input value={newEventForm.location} onChange={(e) => setNewEventForm((p) => ({ ...p, location: e.target.value }))} placeholder="NCOF Main Hall" />
                </Field>
                <Field>
                  <label>Ministry</label>
                  <input value={newEventForm.ministry} onChange={(e) => setNewEventForm((p) => ({ ...p, ministry: e.target.value }))} onFocus={() => setIsMinistryDropdownOpen(true)} onBlur={() => window.setTimeout(() => setIsMinistryDropdownOpen(false), 120)} placeholder="Select ministry/department" />
                  {isMinistryDropdownOpen ? (
                    <DropdownList>
                      {filteredMinistryOptions.length > 0 ? filteredMinistryOptions.map((label) => (
                        <DropdownItem key={label} onMouseDown={() => { setNewEventForm((p) => ({ ...p, ministry: label })); setIsMinistryDropdownOpen(false); }}>{label}</DropdownItem>
                      )) : <DropdownEmpty>No matches found.</DropdownEmpty>}
                    </DropdownList>
                  ) : null}
                </Field>
              </EditorGrid>
              <Field>
                <label>Description</label>
                <textarea rows={3} value={newEventForm.description} onChange={(e) => setNewEventForm((p) => ({ ...p, description: e.target.value }))} placeholder="Event details" />
              </Field>
              <Field>
                <label>Event Media</label>
                <HelperText>Optional image and/or external video URL.</HelperText>
                {newEventForm.image_url ? <ImagePreview src={newEventForm.image_url} alt="Event upload preview" /> : null}
                <EventMediaEditor
                  mediaItems={newEventForm.media_items}
                  isUploading={isUploadingCreateEventImage || isCreatingEvent}
                  onUploadImage={handleCreateEventImageUpload}
                  onAddVideoUrl={handleAddCreateEventVideo}
                  onMoveItem={handleMoveCreateEventMedia}
                  onRemoveItem={handleRemoveCreateEventMedia}
                />
              </Field>
              {createEventError ? <ErrorText>{createEventError}</ErrorText> : null}
              <ButtonRow>
                <ActionButton type="submit" disabled={isCreatingEvent}>{isCreatingEvent ? 'Creating…' : 'Create Event'}</ActionButton>
              </ButtonRow>
            </ManageCard>
          </CreateModalContent>
        </ModalOverlay>
      ) : null}

      {calendarPreviewRequest ? (
        <ModalOverlay onClick={() => setCalendarPreviewRequest(null)}>
          <PreviewModalContent onClick={(e) => e.stopPropagation()}>
            <CreateModalHeader>
              <h3>Event Preview</h3>
              <ModalCloseButton type="button" onClick={() => setCalendarPreviewRequest(null)}>
                Close
              </ModalCloseButton>
            </CreateModalHeader>
            <PreviewBody>
              {calendarPreviewRequest.image_url ? (
                <PreviewImageWrapper>
                  <PreviewImage src={calendarPreviewRequest.image_url} alt={calendarPreviewRequest.title ?? 'Event'} />
                </PreviewImageWrapper>
              ) : (
                <PreviewImagePlaceholder />
              )}
              <PreviewTitle>{calendarPreviewRequest.title || 'Untitled'}</PreviewTitle>
              <PreviewMetaRow>
                <PreviewMetaItem>
                  <strong>Date:</strong>{' '}
                  {calendarPreviewRequest.start_date
                    ? new Date(calendarPreviewRequest.start_date).toLocaleDateString(undefined, {
                        weekday: 'short',
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                      })
                    : 'TBD'}
                </PreviewMetaItem>
                <PreviewMetaItem>
                  <strong>Time:</strong> {formatTime(calendarPreviewRequest.start_time)}
                </PreviewMetaItem>
              </PreviewMetaRow>
              {calendarPreviewRequest.location ? (
                <PreviewMetaItem>
                  <strong>Location:</strong> {calendarPreviewRequest.location}
                </PreviewMetaItem>
              ) : null}
              {calendarPreviewRequest.ministry ? (
                <PreviewMetaItem>
                  <strong>Ministry:</strong> {calendarPreviewRequest.ministry}
                </PreviewMetaItem>
              ) : null}
              {calendarPreviewRequest.description ? (
                <PreviewDescription>{calendarPreviewRequest.description}</PreviewDescription>
              ) : null}
              <PreviewStatusBadge
                $bg={STATUS_CALENDAR_COLORS[getStatusKey(calendarPreviewRequest)]}
                $text={STATUS_CALENDAR_TEXT[getStatusKey(calendarPreviewRequest)]}
              >
                {TABS.find((t) => t.key === getStatusKey(calendarPreviewRequest))?.label ?? 'Pending'}
              </PreviewStatusBadge>
            </PreviewBody>
            <PreviewActions>
              {calendarPreviewRequest.event_id ? (
                <ActionButton
                  type="button"
                  onClick={() => {
                    navigate(`/events/setup/${calendarPreviewRequest.event_id}`);
                    setCalendarPreviewRequest(null);
                  }}
                >
                  Open Event
                </ActionButton>
              ) : (
                <ActionButton
                  type="button"
                  onClick={() => {
                    setActiveTab(getStatusKey(calendarPreviewRequest));
                    setViewMode('list');
                    setCalendarPreviewRequest(null);
                  }}
                >
                  View in List
                </ActionButton>
              )}
            </PreviewActions>
          </PreviewModalContent>
        </ModalOverlay>
      ) : null}
    </Page>
  );
};

export default EventsPage;

const SectionHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1.25rem;
  flex-wrap: wrap;
`;

const HeaderLeft = styled.div``;
const HeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
`;

const ViewToggle = styled.div`
  display: flex;
  border: 1px solid var(--outline);
  border-radius: 8px;
  overflow: hidden;
  background: var(--surface);
`;
const ViewToggleBtn = styled.button`
  padding: 0.5rem 0.85rem;
  border: none;
  background: var(--surface);
  color: var(--onSurfaceVariant);
  font-weight: 600;
  font-size: 0.9rem;
  cursor: pointer;
  &[data-active='true'] {
    background: var(--surfaceVariant);
    color: var(--onSurface);
  }
`;
const SyncButton = styled.button`
  padding: 0.45rem 0.75rem;
  border-radius: 8px;
  border: 1px solid var(--outline);
  background: var(--surface);
  color: var(--onSurface);
  font-weight: 600;
  font-size: 0.9rem;
  cursor: pointer;
  &:hover:not(:disabled) {
    background: var(--surfaceVariant);
  }
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const CreateEventButton = styled.button`
  padding: 0.55rem 1rem;
  border-radius: 10px;
  border: 1px solid var(--primaryDark);
  background: var(--primary);
  color: var(--onPrimary);
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  &:hover:not(:disabled) {
    opacity: 0.92;
  }
`;

const TabBadge = styled.span`
  margin-left: 0.35rem;
  font-size: 0.8rem;
  opacity: 0.95;
`;

const EventStatusTabs = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  flex-wrap: wrap;
`;

const EventStatusTabButton = styled.button`
  padding: 0.35rem 0.65rem;
  border-radius: 999px;
  border: 1px solid var(--outline);
  background: var(--surface);
  color: var(--onSurfaceVariant);
  cursor: pointer;
  font-size: 0.78rem;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  transition: all 0.15s ease;

  &[data-active='true'] {
    background: var(--primary);
    color: var(--onPrimary);
    border-color: var(--primaryDark);
  }
`;

const EventStatusTabCount = styled.span`
  font-size: 0.72rem;
  opacity: 0.9;
`;

const PastEventsHint = styled.p`
  margin: -0.5rem 0 1rem;
  color: var(--onSurfaceVariant);
  font-size: 0.86rem;
`;

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1rem;
  flex-wrap: wrap;

  @media (max-width: ${MOBILE_BREAKPOINT}) {
    gap: 0.5rem;
  }
`;
const SearchWrap = styled.label`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex: 1;
  min-width: 200px;
  max-width: 320px;
  padding: 0.5rem 0.75rem;
  border: 1px solid var(--outline);
  border-radius: 8px;
  background: var(--surface);

  @media (max-width: ${MOBILE_BREAKPOINT}) {
    flex: 1 1 100%;
    min-width: 0;
    max-width: none;
  }
`;
const SearchIcon = styled.span`
  color: var(--onSurfaceVariant);
  font-size: 1rem;
`;
const SearchInput = styled.input`
  border: none;
  background: transparent;
  flex: 1;
  font-size: 0.9rem;
  color: var(--onSurface);
  &::placeholder {
    color: var(--onSurfaceVariant);
  }
  &:focus {
    outline: none;
  }
`;
const ToolbarFilterButton = styled.button`
  padding: 0.5rem 0.85rem;
  border: 1px solid var(--outline);
  border-radius: 8px;
  background: var(--surface);
  color: var(--onSurface);
  font-weight: 600;
  cursor: pointer;
`;
const StatusSummary = styled.div`
  display: flex;
  align-items: center;
  gap: 0.6rem;
  flex-wrap: wrap;
  margin-left: auto;

  @media (max-width: ${MOBILE_BREAKPOINT}) {
    display: none;
  }
`;
const StatusSummaryLabel = styled.span`
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--onSurfaceVariant);
`;
const StatusSummaryBadge = styled.span<{ $variant: 'action' | 'approved' | 'onHold' }>`
  font-size: 0.75rem;
  font-weight: 700;
  padding: 0.25rem 0.5rem;
  border-radius: 6px;
  ${({ $variant }) =>
    $variant === 'action' &&
    `
    background: var(--warningContainer);
    color: var(--onWarningContainer);
  `}
  ${({ $variant }) =>
    $variant === 'approved' &&
    `
    background: var(--successContainer);
    color: var(--onSuccessContainer);
  `}
  ${({ $variant }) =>
    $variant === 'onHold' &&
    `
    background: var(--surfaceVariant);
    color: var(--onSurfaceVariant);
  `}
`;
const CardList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-bottom: 1.5rem;
`;

const RequestFocusWrap = styled.div<{ $highlighted: boolean }>`
  border-radius: 16px;
  transition: box-shadow 0.18s ease, transform 0.18s ease;

  ${({ $highlighted }) =>
    $highlighted
      ? `
    box-shadow: 0 0 0 3px color-mix(in srgb, var(--primary) 30%, transparent);
    transform: translateY(-1px);
  `
      : ''}
`;

const LoadingMessage = styled.p`
  color: var(--onSurfaceVariant);
  margin: 0 0 1rem;
`;
const CalendarWrap = styled.div`
  background: var(--surface);
  border: 1px solid var(--outline);
  border-radius: 12px;
  padding: 1.25rem;
  margin-bottom: 1.5rem;

  @media (max-width: ${MOBILE_BREAKPOINT}) {
    padding: 0.75rem;
  }
`;

const CalendarHeaderRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1.25rem;

  @media (max-width: ${MOBILE_BREAKPOINT}) {
    gap: 0.5rem;
    margin-bottom: 0.75rem;
  }
`;

const CalendarMonthLabel = styled.h3`
  margin: 0;
  font-size: 1.25rem;
  font-weight: 700;
  color: var(--onSurface);

  @media (max-width: ${MOBILE_BREAKPOINT}) {
    font-size: 1.1rem;
  }
`;

const CalendarNav = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const CalendarNavBtn = styled.button`
  width: 2.25rem;
  height: 2.25rem;
  border: 1px solid var(--outline);
  border-radius: 8px;
  background: var(--surfaceVariant);
  color: var(--onSurface);
  font-size: 1.25rem;
  line-height: 1;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  &:hover {
    background: var(--outline);
  }
`;

const CalendarTodayBtn = styled.button`
  padding: 0.4rem 0.75rem;
  border: 1px solid var(--primaryDark);
  border-radius: 8px;
  background: var(--primary);
  color: var(--onPrimary);
  font-weight: 600;
  font-size: 0.9rem;
  cursor: pointer;
  &:hover {
    opacity: 0.92;
  }
`;

const CalendarLegend = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.75rem 1rem;

  @media (max-width: ${MOBILE_BREAKPOINT}) {
    overflow-x: auto;
    flex-wrap: nowrap;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: thin;
    gap: 0.5rem 0.75rem;
  }
`;

const CalendarLegendButton = styled.button<{ $hidden?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  font-size: 0.8rem;
  color: var(--onSurfaceVariant);
  cursor: pointer;
  border: none;
  background: none;
  padding: 0;
  &:hover {
    opacity: 0.92;
  }
  ${({ $hidden }) =>
    $hidden &&
    `
    opacity: 0.5;
    text-decoration: line-through;
  `}
`;

const CalendarLegendDot = styled.span<{ $color: string }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${({ $color }) => $color};
`;

const CalendarGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
`;

const CalendarWeekRow = styled.div<{ $header?: boolean }>`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  min-height: ${({ $header }) => ($header ? 'auto' : '100px')};
  ${({ $header }) => $header && 'margin-bottom: 0.5rem;'}

  @media (max-width: ${MOBILE_BREAKPOINT}) {
    min-height: ${({ $header }) => ($header ? 'auto' : '65px')};
  }
`;

const CalendarDayHead = styled.div`
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--onSurfaceVariant);
  padding: 0.25rem 0.35rem;
  text-align: center;

  @media (max-width: ${MOBILE_BREAKPOINT}) {
    padding: 0.2rem 0.15rem;
    font-size: 0.65rem;
  }
`;

const CalendarDayCell = styled.div<{ $faded?: boolean }>`
  border: 1px solid var(--outline);
  padding: 0.35rem;
  background: var(--background);
  min-height: 96px;
  min-width: 0;
  overflow: hidden;
  ${({ $faded }) => $faded && 'opacity: 0.5; background: var(--surfaceVariant);'}

  @media (max-width: ${MOBILE_BREAKPOINT}) {
    min-height: 60px;
    padding: 0.2rem 0.15rem;
  }
`;

const CalendarDayNumber = styled.span<{ $today?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.75rem;
  height: 1.75rem;
  border-radius: 50%;
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--onSurface);
  ${({ $today }) =>
    $today &&
    `
    background: var(--primary);
    color: var(--onPrimary);
  `}

  @media (max-width: ${MOBILE_BREAKPOINT}) {
    width: 1.4rem;
    height: 1.4rem;
    font-size: 0.8rem;
  }
`;

const CalendarDayEvents = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  margin-top: 0.35rem;
  min-width: 0;
  overflow: hidden;
`;

const CalendarEventChip = styled.button<{ $bg: string; $text: string }>`
  text-align: left;
  padding: 0.25rem 0.4rem;
  border-radius: 6px;
  border: none;
  background: ${({ $bg }) => $bg};
  color: ${({ $text }) => $text};
  font-size: 0.72rem;
  line-height: 1.2;
  cursor: pointer;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
  min-width: 0;
  box-sizing: border-box;
  &:hover {
    filter: brightness(0.95);
  }

  @media (max-width: ${MOBILE_BREAKPOINT}) {
    font-size: 0.65rem;
    padding: 0.2rem 0.3rem;
  }
`;

const UpcomingStrip = styled.div`
  display: flex;
  gap: 1rem;
  overflow-x: auto;
  padding-bottom: 0.25rem;
  margin-bottom: 0.25rem;
  -webkit-overflow-scrolling: touch;
  scroll-snap-type: x mandatory;
  scroll-behavior: smooth;
  scrollbar-width: none;
  -ms-overflow-style: none;
  &::-webkit-scrollbar {
    display: none;
  }
`;

const UpcomingCard = styled.article`
  flex: 0 0 280px;
  min-width: 280px;
  background: var(--surface);
  border-radius: 14px;
  overflow: hidden;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.06);
  scroll-snap-align: start;
  position: relative;
`;

const UpcomingCardOpenButton = styled.button`
  border: none;
  width: 100%;
  background: transparent;
  padding: 0;
  cursor: pointer;
  text-align: left;
  display: flex;
  flex-direction: column;
  align-items: stretch;
`;

const UpcomingScrollWrap = styled.div`
  position: relative;
`;

const ScrollArrowButton = styled.button<{ $right?: boolean }>`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  z-index: 2;
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 50%;
  border: 1px solid var(--outline);
  background: var(--surface);
  color: var(--onSurface);
  font-size: 1.5rem;
  line-height: 1;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  left: ${({ $right }) => ($right ? 'auto' : '0.5rem')};
  right: ${({ $right }) => ($right ? '0.5rem' : 'auto')};
  &:hover {
    background: var(--surfaceVariant);
  }
`;

const UpcomingImageWrapper = styled.div`
  position: relative;
  width: 100%;
  height: 160px;
  flex-shrink: 0;
  overflow: hidden;
`;

const UpcomingImage = styled.img`
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
`;

const UpcomingImagePlaceholder = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(135deg, var(--primary), var(--primaryDark));
`;

const UpcomingDateBadge = styled.div`
  position: absolute;
  bottom: 0.75rem;
  right: 0.75rem;
  padding: 0.25rem 0.4rem;
  border-radius: 10px;
  background: rgba(0, 0, 0, 0.8);
  color: white;
  text-align: center;
  min-width: 2.4rem;

  .month {
    display: block;
    font-size: 0.6rem;
    text-transform: uppercase;
    letter-spacing: 0.06em;
  }

  .day {
    display: block;
    font-size: 0.95rem;
    font-weight: 700;
    line-height: 1.1;
  }
`;

const UpcomingBody = styled.div`
  padding: 0.85rem 0.9rem 0.95rem;
`;

const UpcomingTitle = styled.h3<{ title?: string }>`
  margin: 0 0 0.35rem;
  font-size: 0.98rem;
  font-weight: 700;
  color: var(--onSurface);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const UpcomingMeta = styled.div`
  display: flex;
  flex-wrap: nowrap;
  gap: 0.35rem;
  align-items: center;
  font-size: 0.78rem;
  color: var(--onSurfaceVariant);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const UpcomingTagRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.3rem;
  margin-top: 0.5rem;
`;

const UpcomingTag = styled.span<{ $variant?: 'subtle' }>`
  font-size: 0.7rem;
  font-weight: 700;
  padding: 0.18rem 0.45rem;
  border-radius: 999px;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  ${({ $variant }) =>
    $variant === 'subtle'
      ? `
    background: var(--surfaceVariant);
    color: var(--onSurfaceVariant);
  `
      : `
    background: var(--primaryContainer);
    color: var(--onPrimaryContainer);
  `}
`;

const UpcomingStatusTag = styled.span<{ $status: Exclude<EventStatusFilter, 'all'> }>`
  font-size: 0.68rem;
  font-weight: 700;
  padding: 0.18rem 0.45rem;
  border-radius: 999px;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  ${({ $status }) =>
    $status === 'draft'
      ? `
    background: var(--warningContainer);
    color: var(--onWarningContainer);
  `
      : $status === 'past'
        ? `
    background: var(--surfaceVariant);
    color: var(--onSurfaceVariant);
  `
        : `
    background: var(--successContainer);
    color: var(--onSuccessContainer);
  `}
`;

const UpcomingMenuWrap = styled.div`
  position: absolute;
  top: 0.6rem;
  right: 0.6rem;
  z-index: 3;
`;

const UpcomingMenuButton = styled.button`
  width: 2rem;
  height: 2rem;
  border-radius: 999px;
  border: 1px solid var(--outline);
  background: rgba(0, 0, 0, 0.62);
  color: #fff;
  font-size: 1.1rem;
  line-height: 1;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

const UpcomingMenuList = styled.div`
  position: absolute;
  top: calc(100% + 0.35rem);
  right: 0;
  min-width: 150px;
  background: var(--surface);
  border: 1px solid var(--outline);
  border-radius: 10px;
  box-shadow: 0 10px 24px rgba(0, 0, 0, 0.16);
  padding: 0.3rem;
`;

const UpcomingMenuItem = styled.button`
  width: 100%;
  border: none;
  background: transparent;
  text-align: left;
  padding: 0.5rem 0.55rem;
  border-radius: 7px;
  color: var(--onSurface);
  cursor: pointer;
  font-weight: 600;
  font-size: 0.83rem;

  &[data-variant='danger'] {
    color: var(--error);
  }

  &:hover:not(:disabled) {
    background: var(--surfaceVariant);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const UpcomingMessage = styled.p`
  margin: 0;
  font-size: 0.85rem;
  color: var(--onSurfaceVariant);
`;

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  display: grid;
  place-items: center;
  z-index: 1000;
  padding: 1rem;
`;
const CreateModalContent = styled.div`
  width: min(720px, 100%);
  max-height: calc(100vh - 2rem);
  overflow: auto;
  background: var(--surface);
  border: 1px solid var(--outline);
  border-radius: 12px;
  padding: 1.25rem;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
`;
const CreateModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  margin-bottom: 1rem;
  h3 {
    margin: 0;
    font-size: 1.25rem;
    color: var(--onSurface);
  }
`;
const ModalCloseButton = styled.button`
  padding: 0.4rem 0.75rem;
  border: 1px solid var(--outline);
  border-radius: 8px;
  background: var(--surfaceVariant);
  color: var(--onSurface);
  font-weight: 600;
  cursor: pointer;
`;

const PreviewModalContent = styled.div`
  width: min(720px, 92vw);
  height: 90vh;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: var(--surface);
  border: 1px solid var(--outline);
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
`;
const PreviewImageWrapper = styled.div`
  width: 100%;
  aspect-ratio: 16 / 9;
  border-radius: 10px;
  overflow: hidden;
  background: var(--surfaceVariant);
  margin-bottom: 0.25rem;
`;
const PreviewImage = styled.img`
  display: block;
  width: 100%;
  height: 100%;
  object-fit: contain;
`;
const PreviewImagePlaceholder = styled.div`
  width: 100%;
  aspect-ratio: 16 / 9;
  border-radius: 10px;
  background: var(--surfaceVariant);
  margin-bottom: 0.25rem;
`;
const PreviewBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  flex: 1;
  min-height: 0;
  overflow: auto;
`;
const PreviewTitle = styled.h4`
  margin: 0;
  font-size: 1.15rem;
  color: var(--onSurface);
  line-height: 1.35;
`;
const PreviewMetaRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem 1rem;
`;
const PreviewMetaItem = styled.span`
  font-size: 0.9rem;
  color: var(--onSurfaceVariant);
  strong {
    color: var(--onSurface);
  }
`;
const PreviewDescription = styled.p`
  margin: 0;
  font-size: 0.9rem;
  color: var(--onSurfaceVariant);
  line-height: 1.45;
  white-space: pre-wrap;
  max-height: 6em;
  overflow: auto;
`;
const PreviewStatusBadge = styled.span<{ $bg: string; $text: string }>`
  display: inline-block;
  padding: 0.25rem 0.5rem;
  border-radius: 6px;
  font-size: 0.75rem;
  font-weight: 600;
  background: ${({ $bg }) => $bg};
  color: ${({ $text }) => $text};
  width: fit-content;
`;
const PreviewActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  flex-shrink: 0;
  padding-top: 1rem;
  margin-top: 1rem;
  border-top: 1px solid var(--outline);
`;

const ActionButton = styled.button`
  padding: 0.55rem 0.9rem;
  border-radius: 10px;
  border: 1px solid var(--primaryDark);
  background: var(--primary);
  color: var(--onPrimary);
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.15s ease, transform 0.05s ease;

  &:hover:not(:disabled) {
    opacity: 0.9;
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const LastSync = styled.div`
  font-size: 0.9rem;
  color: var(--onSurfaceVariant);
`;

const EmptyState = styled.div`
  padding: 1rem;
  border: 1px dashed var(--outline);
  border-radius: 10px;
  color: var(--onSurfaceVariant);
  margin-bottom: 1.5rem;
  background: var(--surfaceVariant);
`;

const Divider = styled.hr`
  border: none;
  border-top: 1px solid var(--outline);
  margin: 2.25rem 0;
`;

type TabKey = 'pending' | 'approvals' | 'needs_info' | 'on_hold' | 'completed' | 'declined';

function isEventsTabKey(value: string | null | undefined): value is TabKey {
  return TABS.some((tab) => tab.key === value);
}
type EventStatusFilter = 'all' | 'published' | 'draft' | 'past';

const EVENT_STATUS_TABS: { key: EventStatusFilter; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'published', label: 'Published' },
  { key: 'draft', label: 'Drafts' },
  { key: 'past', label: 'Past' },
];

const TABS: { key: TabKey; label: string; statuses: string[] }[] = [
  { key: 'pending', label: 'Pending', statuses: ['pending'] },
  { key: 'approvals', label: 'Approvals', statuses: ['approved'] },
  { key: 'needs_info', label: 'Needs Info', statuses: ['needs_info', 'contact_requestor'] },
  { key: 'on_hold', label: 'On Hold', statuses: ['on_hold'] },
  { key: 'completed', label: 'Completed', statuses: ['completed'] },
  { key: 'declined', label: 'Declined', statuses: ['declined'] },
];

const STATUS_CALENDAR_COLORS: Record<TabKey, string> = {
  pending: '#e6b800',
  approvals: '#2e7d32',
  needs_info: '#1565c0',
  on_hold: '#ef6c00',
  completed: '#616161',
  declined: '#c62828',
};

const STATUS_CALENDAR_TEXT: Record<TabKey, string> = {
  pending: '#1a1a1a',
  approvals: '#fff',
  needs_info: '#fff',
  on_hold: '#fff',
  completed: '#fff',
  declined: '#fff',
};

const CALENDAR_DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const CALENDAR_DAY_NAMES_SHORT = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

const Tabs = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin: 0 0 1rem;

  @media (max-width: ${MOBILE_BREAKPOINT}) {
    flex-wrap: nowrap;
    overflow-x: auto;
    overflow-y: hidden;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: thin;
    margin-bottom: 1rem;
  }
`;

const TabButton = styled.button`
  padding: 0.45rem 0.75rem;
  border-radius: 10px;
  border: 1px solid var(--outline);
  background: var(--surface);
  cursor: pointer;
  font-weight: 600;
  color: var(--onSurfaceVariant);
  transition: all 0.15s ease;

  &[data-active='true'] {
    background: var(--primary);
    color: var(--onPrimary);
    border-color: var(--primaryDark);
  }

  @media (max-width: ${MOBILE_BREAKPOINT}) {
    flex-shrink: 0;
  }
`;

const ManageCard = styled.div`
  background: var(--surface);
  border: 1px solid var(--outline);
  border-radius: 10px;
  padding: 1.5rem;
`;

const EditorGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 0.6rem;
  margin-bottom: 0.6rem;
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;

  label {
    font-size: 0.78rem;
    font-weight: 600;
    color: var(--onSurfaceVariant);
  }

  input,
  select,
  textarea {
    border: 1px solid var(--outline);
    border-radius: 8px;
    padding: 0.45rem 0.55rem;
    font-size: 0.86rem;
    background: var(--surface);
    color: var(--onSurface);
  }

  input::placeholder,
  textarea::placeholder {
    color: var(--onSurfaceVariant);
  }

  textarea {
    resize: vertical;
    min-height: 60px;
  }
`;

const ErrorText = styled.p`
  margin: 0.25rem 0;
  color: var(--error);
  font-weight: 600;
`;

const HelperText = styled.span`
  color: var(--onSurfaceVariant);
  font-size: 0.82rem;
`;

const DropdownList = styled.ul`
  list-style: none;
  margin: 0.35rem 0 0;
  padding: 0;
  border: 1px solid var(--outline);
  border-radius: 8px;
  max-height: 180px;
  overflow: auto;
  background: var(--surface);
`;

const DropdownItem = styled.li`
  padding: 0.55rem 0.75rem;
  cursor: pointer;
  color: var(--onSurface);

  &:hover {
    background: var(--surfaceVariant);
  }
`;

const DropdownEmpty = styled.li`
  padding: 0.55rem 0.75rem;
  color: var(--onSurfaceVariant);
`;

const ImagePreview = styled.img`
  width: 100%;
  max-width: 320px;
  border-radius: 10px;
  border: 1px solid var(--outline);
  object-fit: cover;
`;


const ButtonRow = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 0.75rem;
`;

function isEventDatePast(req: { start_date?: string | null }): boolean {
  const dateStr = (req.start_date ?? '').toString().slice(0, 10);
  if (!dateStr || dateStr.length < 10) return false;
  const today = toISODate(new Date());
  return dateStr < today;
}

function isInTab(
  req: { sheet_status?: string | null; status?: string | null; start_date?: string | null },
  tab: TabKey
) {
  if (isEventDatePast(req)) return tab === 'completed';
  const normalizedStatus = req.status?.toLowerCase().replace(/\s+/g, '_') ?? null;
  const normalizedSheetStatus = req.sheet_status?.toLowerCase().replace(/\s+/g, '_') ?? null;
  const resolvedStatus =
    normalizedStatus && normalizedStatus !== 'pending'
      ? normalizedStatus
      : normalizedSheetStatus ?? normalizedStatus ?? 'pending';
  const tabDef = TABS.find((t) => t.key === tab);
  if (!tabDef) return false;
  return tabDef.statuses.includes(resolvedStatus);
}

function sortByLatest(
  a: { submitted_at?: string | null; created_at?: string | null; id?: string },
  b: { submitted_at?: string | null; created_at?: string | null; id?: string }
) {
  const aDate = parseDate(a.submitted_at) ?? parseDate(a.created_at);
  const bDate = parseDate(b.submitted_at) ?? parseDate(b.created_at);
  if (aDate && bDate) return bDate.getTime() - aDate.getTime();
  if (aDate) return -1;
  if (bDate) return 1;
  return (b.id ?? '').localeCompare(a.id ?? '');
}

function parseDate(value?: string | null): Date | null {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

function formatTime(value?: string | null) {
  if (!value) return 'TBD';
  const [hRaw, mRaw] = value.split(':');
  const hours = Number(hRaw);
  const minutes = Number(mRaw ?? 0);
  if (Number.isNaN(hours) || Number.isNaN(minutes)) return value;
  const displayHour = ((hours + 11) % 12) + 1;
  const suffix = hours >= 12 ? 'PM' : 'AM';
  return `${displayHour}:${String(minutes).padStart(2, '0')} ${suffix}`;
}

function toISODate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

type CalendarDay = { date: Date; isCurrentMonth: boolean };

function getCalendarWeeks(year: number, month: number): CalendarDay[][] {
  const first = new Date(year, month, 1);
  const last = new Date(year, month + 1, 0);
  const startDay = first.getDay();
  const startOffset = startDay;
  const daysInMonth = last.getDate();
  const weeks: CalendarDay[][] = [];
  let week: CalendarDay[] = [];
  for (let i = 0; i < startOffset; i++) {
    const d = new Date(year, month, 1 - (startOffset - i));
    week.push({ date: d, isCurrentMonth: false });
  }
  for (let day = 1; day <= daysInMonth; day++) {
    week.push({ date: new Date(year, month, day), isCurrentMonth: true });
    if (week.length === 7) {
      weeks.push(week);
      week = [];
    }
  }
  if (week.length > 0) {
    let nextDay = new Date(year, month, daysInMonth);
    while (week.length < 7) {
      nextDay = new Date(nextDay.getTime() + 86400000);
      week.push({ date: nextDay, isCurrentMonth: false });
    }
    weeks.push(week);
  }
  return weeks;
}

function getStatusKey(req: {
  status?: string | null;
  sheet_status?: string | null;
  start_date?: string | null;
}): TabKey {
  if (isEventDatePast(req)) return 'completed';
  const s = (req.status ?? req.sheet_status ?? 'pending').toLowerCase().replace(/\s+/g, '_');
  if (TABS.some((t) => t.statuses.includes(s))) {
    const tab = TABS.find((t) => t.statuses.includes(s));
    return tab?.key ?? 'pending';
  }
  return 'pending';
}

function truncateTitle(title: string, maxLen = 18): string {
  if (title.length <= maxLen) return title;
  return title.slice(0, maxLen).trim() + '…';
}

function isGeneralAnnouncementCategory(value?: string | null) {
  const normalized = (value ?? '')
    .toLowerCase()
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  return normalized === 'general announcement' || normalized === 'general annoucement';
}

function formatEventStatusLabel(value?: string | null) {
  return getEventStatusLabel(value);
}

function getDisplayedEventStatus(
  value: string | null | undefined,
  isPast: boolean
): Exclude<EventStatusFilter, 'all'> {
  return isPast ? 'past' : getEventStatusBucket(value);
}

function formatDisplayedEventStatus(value: string | null | undefined, isPast: boolean) {
  return isPast ? 'Past' : formatEventStatusLabel(value);
}

function getEventStatusBucket(value?: string | null): Exclude<EventStatusFilter, 'all' | 'past'> {
  const normalized = (value ?? '').trim().toLowerCase();
  if (normalized === 'published' || normalized === 'approved') {
    return 'published';
  }
  return 'draft';
}

function getEventStatusLabel(value?: string | null) {
  const normalized = (value ?? '').trim().toLowerCase();
  if (normalized === 'approved') {
    return 'Approved';
  }
  return getEventStatusBucket(normalized) === 'published' ? 'Published' : 'Draft';
}
